<template>
  <div class="admin-projects">
    <!-- Header -->
    <div class="admin-header">
      <h1 class="admin-title">
        <i class="fas fa-building"></i>
        Proje Yönetimi
      </h1>
      <button @click="openCreateModal" class="btn btn-create">
        <i class="fas fa-plus"></i>
        Yeni Proje Ekle
      </button>
      <button @click="toggleApiMode" class="btn" :class="USE_MOCK_API ? 'btn-cancel' : 'btn-submit'">
        <i class="fas fa-database"></i>
        {{ USE_MOCK_API ? 'Mock API' : 'Real API' }}
      </button>
    </div>

    <!-- Statistics Cards -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fas fa-building"></i>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ totalProjects }}</div>
          <div class="stat-label">Toplam Proje</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon featured">
          <i class="fas fa-star"></i>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ featuredProjects }}</div>
          <div class="stat-label">Öne Çıkan</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon completed">
          <i class="fas fa-check-circle"></i>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ completedProjects }}</div>
          <div class="stat-label">Tamamlanan</div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="admin-filters">
      <div class="search-box">
        <i class="fas fa-search"></i>
        <input 
          v-model="searchQuery" 
          @input="debouncedSearch"
          type="text" 
          placeholder="Proje başlığı veya açıklama ile ara..."
        >
      </div>
      <div class="filter-actions">
        <select v-model="filterCategory" @change="loadProjects">
          <option value="">Tüm Kategoriler</option>
          <option value="solar">Güneş Enerjisi</option>
          <option value="wind">Rüzgar Enerjisi</option>
          <option value="hydro">Hidroelektrik</option>
          <option value="biomass">Biyokütle</option>
          <option value="other">Diğer</option>
        </select>
        <select v-model="filterFeatured" @change="loadProjects">
          <option value="">Tümü</option>
          <option value="1">Öne Çıkanlar</option>
          <option value="0">Normal</option>
        </select>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Projeler yükleniyor...</p>
    </div>



    <!-- Admin Projects Grid -->
    <div v-if="!loading && projects.length > 0" class="admin-projects-wrapper">
      
      <div class="projects-section">
        <div class="section-header">
          <h2 class="section-title">
            <i :class="filterFeatured === '1' ? 'fas fa-star' : 'fas fa-list'"></i>
            {{ filterFeatured === '1' ? 'Öne Çıkan Projeler' : filterFeatured === '0' ? 'Normal Projeler' : 'Tüm Projeler' }}
            <span class="section-count">({{ projects.length }})</span>
          </h2>
        </div>
        <div class="admin-projects-grid">
          <div 
            v-for="project in projects" 
            :key="project.id"
            :class="['admin-project-card', { 'admin-featured': project.is_featured === 1 || project.is_featured === '1' }]"
          >
            <!-- Image Section -->
            <div class="admin-card-image">
              <img 
                v-if="project.image_url"
                :src="project.image_url"
                :alt="project.title_tr"
                @error="handleImageError"
              />
              <div v-else class="admin-no-image">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                </svg>
              </div>
              <div v-if="project.is_featured === 1 || project.is_featured === '1'" class="admin-featured-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                Öne Çıkan
              </div>
            </div>
            
            <!-- Content Section -->
            <div class="admin-card-content">
              <div class="admin-card-status">
                <span v-if="project.is_featured === 1 || project.is_featured === '1'" class="admin-status-badge admin-featured-status">
                  Öne Çıkan
                </span>
                <span v-else class="admin-status-badge admin-normal-status">
                  Normal
                </span>
                <span class="admin-card-date" v-if="project.completion_date">
                  {{ formatDate(project.completion_date) }}
                </span>
              </div>
              
              <h3 class="admin-card-title">{{ project.title_tr }}</h3>
              <p class="admin-card-excerpt">{{ truncateText(project.description_tr, 100) }}</p>
              
              <div class="admin-card-meta" v-if="project.capacity || project.location_tr || project.category">
                <span v-if="project.capacity" class="admin-meta-item">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14.69 2.21L4.33 11.49c-.64.58-.28 1.65.58 1.73L13 14l.78 8.14c.08.86 1.15 1.22 1.73.58l9.28-10.36c.79-.88-.04-2.22-1.26-2.05L14.69 2.21z"/>
                  </svg>
                  {{ project.capacity }}
                </span>
                <span v-if="project.location_tr" class="admin-meta-item">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                  {{ project.location_tr }}
                </span>
                <span v-if="project.category" class="admin-meta-item">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M5.5 7L3 12.5c0 .83.67 1.5 1.5 1.5S6 13.33 6 12.5L8.5 7H5.5zm5 0L8 12.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5L8.5 7h2zm5 0L13 12.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5L13.5 7H16z"/>
                  </svg>
                  {{ getCategoryName(project.category) }}
                </span>
              </div>
              
              <div class="admin-card-actions">
                <button @click="openEditModal(project)" class="admin-action-btn admin-edit">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                  </svg>
                  Düzenle
                </button>
                <button 
                  @click="toggleFeatured(project)" 
                  :class="['admin-action-btn', 'admin-toggle', (project.is_featured === 1 || project.is_featured === '1') ? 'admin-unfeature' : 'admin-feature']"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path v-if="project.is_featured === 1 || project.is_featured === '1'" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    <path v-else d="M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z"/>
                  </svg>
                  {{ (project.is_featured === 1 || project.is_featured === '1') ? 'Normal Yap' : 'Öne Çıkar' }}
                </button>
                <button @click="deleteProject(project)" class="admin-action-btn admin-delete">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                  </svg>
                  Sil
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      
    </div>

    <!-- Empty State -->
    <div v-if="!loading && projects.length === 0" class="empty-state">
      <i class="fas fa-building"></i>
      <h3>Henüz proje yok</h3>
      <p>İlk projeyi eklemek için yukarıdaki "Yeni Proje Ekle" butonunu kullanın.</p>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.total_pages > 1" class="pagination">
      <button 
        @click="changePage(pagination.current_page - 1)"
        :disabled="pagination.current_page <= 1"
        class="btn btn-page"
      >
        <i class="fas fa-chevron-left"></i>
        Önceki
      </button>
      
      <div class="page-numbers">
        <button 
          v-for="page in visiblePages" 
          :key="page"
          @click="changePage(page)"
          :class="['btn', 'btn-page', { active: page === pagination.current_page }]"
        >
          {{ page }}
        </button>
      </div>
      
      <button 
        @click="changePage(pagination.current_page + 1)"
        :disabled="pagination.current_page >= pagination.total_pages"
        class="btn btn-page"
      >
        Sonraki
        <i class="fas fa-chevron-right"></i>
      </button>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>
            <i class="fas fa-building"></i>
            {{ isEditing ? 'Proje Düzenle' : 'Yeni Proje Ekle' }}
          </h2>
          <button @click="closeModal" class="btn btn-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <form @submit.prevent="submitForm" class="modal-form">
          <div class="form-grid">
            <!-- Türkçe Başlık -->
            <div class="form-group">
              <label for="title_tr" class="required">Başlık (Türkçe)</label>
              <input 
                id="title_tr"
                v-model="form.title_tr" 
                type="text" 
                required
                placeholder="Proje başlığını girin..."
              >
            </div>
            
            <!-- İngilizce Başlık -->
            <div class="form-group">
              <label for="title_en" class="required">Başlık (İngilizce)</label>
              <input 
                id="title_en"
                v-model="form.title_en" 
                type="text" 
                required
                placeholder="Project title..."
              >
            </div>
          </div>
          
          <!-- Türkçe Açıklama -->
          <div class="form-group">
            <label for="description_tr" class="required">Açıklama (Türkçe)</label>
            <textarea 
              id="description_tr"
              v-model="form.description_tr" 
              required
              rows="4"
              placeholder="Proje açıklamasını girin..."
            ></textarea>
          </div>
          
          <!-- İngilizce Açıklama -->
          <div class="form-group">
            <label for="description_en" class="required">Açıklama (İngilizce)</label>
            <textarea 
              id="description_en"
              v-model="form.description_en" 
              required
              rows="4"
              placeholder="Project description..."
            ></textarea>
          </div>
          
          <!-- Görsel URL -->
          <div class="form-group">
            <label for="image_url">Görsel URL</label>
            <input 
              id="image_url"
              v-model="form.image_url" 
              type="url" 
              placeholder="https://example.com/image.jpg"
            >
            <div v-if="form.image_url" class="image-preview">
              <img :src="form.image_url" alt="Önizleme" @error="handleImageError">
            </div>
          </div>
          
          <div class="form-grid">
            <!-- Kapasite -->
            <div class="form-group">
              <label for="capacity">Kapasite</label>
              <input 
                id="capacity"
                v-model="form.capacity" 
                type="text" 
                placeholder="örn: 100 MW"
              >
            </div>
            
            <!-- Kategori -->
            <div class="form-group">
              <label for="category">Kategori</label>
              <select id="category" v-model="form.category">
                <option value="">Kategori Seçin</option>
                <option value="solar">Güneş Enerjisi</option>
                <option value="wind">Rüzgar Enerjisi</option>
                <option value="hydro">Hidroelektrik</option>
                <option value="biomass">Biyokütle</option>
                <option value="other">Diğer</option>
              </select>
            </div>
          </div>
          
          <div class="form-grid">
            <!-- Türkçe Konum -->
            <div class="form-group">
              <label for="location_tr">Konum (Türkçe)</label>
              <input 
                id="location_tr"
                v-model="form.location_tr" 
                type="text" 
                placeholder="İstanbul, Türkiye"
              >
            </div>
            
            <!-- İngilizce Konum -->
            <div class="form-group">
              <label for="location_en">Konum (İngilizce)</label>
              <input 
                id="location_en"
                v-model="form.location_en" 
                type="text" 
                placeholder="Istanbul, Turkey"
              >
            </div>
          </div>
          
          <!-- Tamamlanma Tarihi -->
          <div class="form-group">
            <label for="completion_date">Tamamlanma Tarihi</label>
            <input 
              id="completion_date"
              v-model="form.completion_date" 
              type="date"
            >
          </div>
          
          <!-- Öne Çıkan -->
          <div class="form-group checkbox-group">
            <label>
              <input 
                v-model="form.is_featured" 
                type="checkbox"
              >
              <span class="checkmark"></span>
              Öne çıkan proje olarak işaretle
            </label>
          </div>
          
          <div class="modal-actions">
            <button type="button" @click="closeModal" class="btn btn-cancel">
              İptal
            </button>
            <button type="submit" :disabled="submitting" class="btn btn-submit">
              <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-save"></i>
              {{ isEditing ? 'Güncelle' : 'Kaydet' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { adminApi } from '@/services/adminApi'

export default {
  name: 'AdminProjects',
  emits: ['statsUpdated'],
  setup(props, { emit }) {
    // Reactive data
    const projects = ref([])
    const loading = ref(false)
    const submitting = ref(false)
    const showModal = ref(false)
    const isEditing = ref(false)
    const searchQuery = ref('')
    const filterCategory = ref('')
    const filterFeatured = ref('')
    const USE_MOCK_API = ref(false) // Can be toggled by user
    
    // Total stats (dropdown bağımsız)
    const totalAllProjects = ref(0)
    const totalFeaturedProjects = ref(0)
    
    // Pagination
    const pagination = ref({
      current_page: 1,
      per_page: 12,
      total: 0,
      total_pages: 1
    })
    
    // Form data
    const form = ref({
      id: null,
      title_tr: '',
      title_en: '',
      description_tr: '',
      description_en: '',
      image_url: '',
      capacity: '',
      location_tr: '',
      location_en: '',
      completion_date: '',
      category: '',
      is_featured: false
    })
    
    // Computed properties
    const totalProjects = computed(() => totalAllProjects.value) // Dropdown bağımsız toplam
    const featuredProjects = computed(() => totalFeaturedProjects.value) // Dropdown bağımsız featured
    const completedProjects = computed(() => projects.value.filter(p => p.completion_date).length)
    
    // Separate featured and normal projects for display
    const featuredProjectsList = computed(() => {
      if (filterFeatured.value === '0') return [] // Normal seçiliyse öne çıkanları gösterme
      return projects.value.filter(p => p.is_featured === 1 || p.is_featured === '1')
    })
    
    const normalProjectsList = computed(() => {
      if (filterFeatured.value === '1') return [] // Öne çıkanlar seçiliyse normalleri gösterme
      return projects.value.filter(p => p.is_featured === 0 || p.is_featured === '0')
    })
    
    const visiblePages = computed(() => {
      const current = pagination.value.current_page
      const total = pagination.value.total_pages
      const pages = []
      
      let start = Math.max(1, current - 2)
      let end = Math.min(total, current + 2)
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      
      return pages
    })
    
    // Debounced search
    let searchTimeout = null
    const debouncedSearch = () => {
      clearTimeout(searchTimeout)
      searchTimeout = setTimeout(() => {
        pagination.value.current_page = 1
        loadProjects()
      }, 500)
    }
    
    // Methods
    const loadProjects = async () => {
      loading.value = true
      try {
        const params = {
          page: pagination.value.current_page,
          limit: pagination.value.per_page,
          search: searchQuery.value,
          category: filterCategory.value,
          featured: filterFeatured.value
        }
        
        const response = await adminApi.getProjects(params)
        
        // Extract data like in ProjectDetail
        if (response && response.success && response.data) {
          projects.value = response.data.projects || []
          pagination.value = response.data.pagination || {
            current_page: 1,
            per_page: 10,
            total: 0,
            total_pages: 1
          }
          
          // Dropdown bağımsız toplam sayıları güncelle
          totalAllProjects.value = response.data.total_all_projects || 0
          totalFeaturedProjects.value = response.data.total_featured_projects || 0
        } else {
          console.error('Invalid API response structure:', response)
          projects.value = []
          pagination.value = {
            current_page: 1,
            per_page: 10,
            total: 0,
            total_pages: 1
          }
          totalAllProjects.value = 0
          totalFeaturedProjects.value = 0
        }
        
        // Emit stats to parent dashboard - dropdown bağımsız toplam sayılar
        emit('statsUpdated', {
          total: response?.data?.total_all_projects || 0, // Tüm projeler (dropdown bağımsız)
          featured: response?.data?.total_featured_projects || 0 // Tüm featured projeler (dropdown bağımsız)
        })
      } catch (error) {
        console.error('Projeler yüklenemedi:', error)
        
        // More detailed error message
        let errorMessage = 'Projeler yüklenirken bir hata oluştu'
        if (error.response?.status === 401) {
          errorMessage = 'Oturum süreniz dolmuş. Lütfen yeniden giriş yapın.'
        } else if (error.code === 'ERR_NETWORK') {
          errorMessage = 'API sunucusuna bağlanılamadı. İnternet bağlantınızı kontrol edin.'
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        }
        
        alert(errorMessage)
      } finally {
        loading.value = false
      }
    }
    
    const openCreateModal = () => {
      isEditing.value = false
      resetForm()
      showModal.value = true
    }
    
    const openEditModal = (project) => {
      isEditing.value = true
      form.value = {
        id: project.id,
        title_tr: project.title_tr,
        title_en: project.title_en,
        description_tr: project.description_tr,
        description_en: project.description_en,
        image_url: project.image_url || '',
        capacity: project.capacity || '',
        location_tr: project.location_tr || '',
        location_en: project.location_en || '',
        completion_date: project.completion_date || '',
        category: project.category || '',
        is_featured: Boolean(project.is_featured)
      }
      showModal.value = true
    }
    
    const closeModal = () => {
      showModal.value = false
      resetForm()
    }
    
    const resetForm = () => {
      form.value = {
        id: null,
        title_tr: '',
        title_en: '',
        description_tr: '',
        description_en: '',
        image_url: '',
        capacity: '',
        location_tr: '',
        location_en: '',
        completion_date: '',
        category: '',
        is_featured: false
      }
    }
    
    const submitForm = async () => {
      submitting.value = true
      try {
        if (isEditing.value) {
          await adminApi.updateProject(form.value.id, form.value)
          alert('Proje başarıyla güncellendi')
        } else {
          await adminApi.createProject(form.value)
          alert('Proje başarıyla oluşturuldu')
        }
        
        closeModal()
        await loadProjects() // This will also emit stats
      } catch (error) {
        console.error('Form gönderilirken hata:', error)
        alert('İşlem sırasında bir hata oluştu')
      } finally {
        submitting.value = false
      }
    }
    
    const deleteProject = async (project) => {
      if (!confirm(`"${project.title_tr}" projesini silmek istediğinizden emin misiniz?`)) {
        return
      }
      
      try {
        await adminApi.deleteProject(project.id)
        alert('Proje başarıyla silindi')
        await loadProjects() // This will also emit stats
      } catch (error) {
        console.error('Proje silinemedi:', error)
        alert('Proje silinirken bir hata oluştu')
      }
    }
    
    const toggleFeatured = async (project) => {
      try {
        // Mevcut durumun tersini hesapla - eğer öne çıkan ise normal yap (0), değilse öne çıkar (1)
        const newFeaturedStatus = project.is_featured ? 0 : 1;
        
        // Sadece gerekli alanları gönder
        const updateData = {
          title_tr: project.title_tr,
          title_en: project.title_en,
          description_tr: project.description_tr,
          description_en: project.description_en,
          image_url: project.image_url || '',
          capacity: project.capacity || '',
          location_tr: project.location_tr || '',
          location_en: project.location_en || '',
          completion_date: project.completion_date || '',
          category: project.category || '',
          is_featured: newFeaturedStatus
        }
        
        await adminApi.updateProject(project.id, updateData)
        
        // Local state'i güncelle
        const index = projects.value.findIndex(p => p.id === project.id)
        if (index !== -1) {
          projects.value[index].is_featured = newFeaturedStatus
        }
        
        // Emit updated stats
        emit('statsUpdated', {
          total: pagination.value.total,
          featured: projects.value.filter(p => p.is_featured).length
        })
        
        alert(`Proje ${newFeaturedStatus ? 'öne çıkarıldı' : 'normalleştirildi'}`)
      } catch (error) {
        console.error('Proje güncellenemedi:', error)
        alert('İşlem sırasında bir hata oluştu')
      }
    }
    
    const changePage = (page) => {
      if (page >= 1 && page <= pagination.value.total_pages) {
        pagination.value.current_page = page
        loadProjects()
      }
    }
    
    const truncateText = (text, length) => {
      if (!text) return ''
      return text.length > length ? text.substring(0, length) + '...' : text
    }
    
    const formatDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('tr-TR')
    }
    
    const formatDateTime = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('tr-TR') + ' ' + date.toLocaleTimeString('tr-TR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    }
    
    const getCategoryName = (category) => {
      const categories = {
        'solar': 'Güneş Enerjisi',
        'wind': 'Rüzgar Enerjisi',
        'hydro': 'Hidroelektrik',
        'biomass': 'Biyokütle',
        'other': 'Diğer'
      }
      return categories[category] || category
    }
    
    const handleImageError = (event) => {
      event.target.src = '/images/default-project.svg'
    }
    
    const toggleApiMode = () => {
      USE_MOCK_API.value = !USE_MOCK_API.value
      // Update adminApi service
      import('@/services/adminApi').then(module => {
        // This is a bit hacky but works for demonstration
        console.log(`Switched to ${USE_MOCK_API.value ? 'Mock' : 'Real'} API mode`)
        alert(`Switched to ${USE_MOCK_API.value ? 'Mock' : 'Real'} API mode. Reloading projects...`)
        loadProjects()
      })
    }
    
    // Lifecycle
    onMounted(() => {
      loadProjects()
    })
    
    return {
      // Data
      projects,
      loading,
      submitting,
      showModal,
      isEditing,
      searchQuery,
      filterCategory,
      filterFeatured,
      pagination,
      form,
      
      // Computed
      totalProjects,
      featuredProjects,
      completedProjects,
      featuredProjectsList,
      normalProjectsList,
      visiblePages,
      
      // Methods
      loadProjects,
      openCreateModal,
      openEditModal,
      closeModal,
      submitForm,
      deleteProject,
      toggleFeatured,
      changePage,
      debouncedSearch,
      truncateText,
      formatDate,
      formatDateTime,
      getCategoryName,
      handleImageError,
      toggleApiMode,
      USE_MOCK_API
    }
  }
}
</script>

<style scoped>
.admin-projects {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

/* Header */
.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #eee;
}

.admin-title {
  font-size: 28px;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.admin-title i {
  color: #3498db;
}

/* Statistics Cards */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  gap: 15px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  background: #3498db;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.stat-icon.featured {
  background: #f39c12;
}

.stat-icon.completed {
  background: #27ae60;
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 5px;
}

.stat-label {
  color: #7f8c8d;
  font-size: 14px;
}

/* Filters */
.admin-filters {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;
  align-items: center;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 400px;
}

.search-box i {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #7f8c8d;
}

.search-box input {
  width: 100%;
  padding: 12px 12px 12px 45px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
}

.search-box input:focus {
  outline: none;
  border-color: #3498db;
}

.filter-actions {
  display: flex;
  gap: 15px;
}

.filter-actions select {
  padding: 12px 15px;
  border: 2px solid #ddd;
  border-radius: 8px;
  background: white;
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.filter-actions select:focus {
  outline: none;
  border-color: #3498db;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #7f8c8d;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Admin Projects Unique Styles */
.admin-projects-wrapper {
  margin-top: 20px;
}

/* Section Headers */
.featured-section, .normal-section {
  margin-bottom: 40px;
}

.section-header {
  margin-bottom: 20px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.section-title i {
  color: #f59e0b;
}

.normal-section .section-title i {
  color: #4a5568;
}

.section-count {
  font-size: 14px;
  font-weight: 500;
  color: #718096;
  background: #f7fafc;
  padding: 4px 8px;
  border-radius: 12px;
}

/* Featured Section Styling */
.featured-section {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 12px;
  padding: 20px;
  border-left: 4px solid #f59e0b;
}

.featured-grid .admin-project-card {
  border: 2px solid #f59e0b;
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.15);
}

/* Normal Section Styling */
.normal-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  border-left: 4px solid #6c757d;
}

.admin-projects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
}

.admin-project-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  transition: all 0.2s;
  position: relative;
  z-index: 1;
}

.admin-project-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.admin-project-card.admin-featured {
  border-color: #f59e0b;
}

.admin-card-image {
  height: 200px;
  overflow: hidden;
  position: relative;
}

.admin-card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.admin-no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7fafc;
  color: #cbd5e0;
}

.admin-featured-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #f59e0b;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.admin-card-content {
  padding: 20px;
}

.admin-card-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.admin-status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.admin-status-badge.admin-featured-status {
  background: #fef3c7;
  color: #92400e;
}

.admin-status-badge.admin-normal-status {
  background: #e2e8f0;
  color: #4a5568;
}

.admin-card-date {
  font-size: 12px;
  color: #718096;
}

.admin-card-title {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  line-height: 1.4;
}

.admin-card-excerpt {
  margin: 0 0 16px 0;
  color: #718096;
  font-size: 14px;
  line-height: 1.5;
}

.admin-card-meta {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.admin-meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #4a5568;
  background: #f7fafc;
  padding: 4px 8px;
  border-radius: 6px;
}

.admin-card-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.admin-action-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.admin-action-btn.admin-edit {
  background: #3182ce;
  color: white;
}

.admin-action-btn.admin-edit:hover {
  background: #2c5aa0;
}

.admin-action-btn.admin-toggle.admin-feature {
  background: #f59e0b;
  color: white;
}

.admin-action-btn.admin-toggle.admin-feature:hover {
  background: #d69e2e;
}

.admin-action-btn.admin-toggle.admin-unfeature {
  background: #e2e8f0;
  color: #4a5568;
}

.admin-action-btn.admin-toggle.admin-unfeature:hover {
  background: #cbd5e0;
}

.admin-action-btn.admin-delete {
  background: #e53e3e;
  color: white;
}

.admin-action-btn.admin-delete:hover {
  background: #c53030;
}

.project-card.featured {
  border: 2px solid #f39c12;
}

/* Removed unused image styles */

.featured-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #f59e0b;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.project-content {
  padding: 20px;
}

.project-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.status-badge.featured {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.normal {
  background: #e2e8f0;
  color: #4a5568;
}

.project-date {
  font-size: 12px;
  color: #718096;
}

.project-title {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  line-height: 1.4;
}

.project-excerpt {
  margin: 0 0 16px 0;
  color: #718096;
  font-size: 14px;
  line-height: 1.5;
}

.project-meta {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.meta-item {
  font-size: 12px;
  color: #4a5568;
  background: #f7fafc;
  padding: 4px 8px;
  border-radius: 6px;
}

.project-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn.edit {
  background: #3182ce;
  color: white;
}

.action-btn.edit:hover {
  background: #2c5aa0;
}

.action-btn.toggle.feature {
  background: #f59e0b;
  color: white;
}

.action-btn.toggle.feature:hover {
  background: #d69e2e;
}

.action-btn.toggle.unfeature {
  background: #e2e8f0;
  color: #4a5568;
}

.action-btn.toggle.unfeature:hover {
  background: #cbd5e0;
}

.action-btn.delete {
  background: #e53e3e;
  color: white;
}

.action-btn.delete:hover {
  background: #c53030;
}

.project-title {
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
  margin: 0 0 10px 0;
  line-height: 1.3;
}

.project-description {
  color: #7f8c8d;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 15px 0;
}

.project-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #7f8c8d;
  background: #f8f9fa;
  padding: 4px 8px;
  border-radius: 4px;
}

.meta-item i {
  color: #3498db;
}

.project-dates {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.project-dates small {
  color: #bdc3c7;
  font-size: 11px;
}

.project-actions {
  position: absolute;
  top: 10px;
  left: 10px;
  display: flex;
  gap: 5px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.project-card:hover .project-actions {
  opacity: 1;
}

.project-actions .btn {
  padding: 8px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;
  color: white;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-edit {
  background: #3498db;
}

.btn-edit:hover {
  background: #2980b9;
}

.btn-featured {
  background: #95a5a6;
}

.btn-featured.active {
  background: #f39c12;
}

.btn-featured:hover {
  background: #f39c12;
}

.btn-delete {
  background: #e74c3c;
}

.btn-delete:hover {
  background: #c0392b;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #7f8c8d;
}

.empty-state i {
  font-size: 64px;
  margin-bottom: 20px;
  color: #bdc3c7;
}

.empty-state h3 {
  font-size: 24px;
  margin-bottom: 10px;
  color: #7f8c8d;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 30px;
}

.btn-page {
  padding: 10px 15px;
  border: 2px solid #ddd;
  background: white;
  color: #7f8c8d;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
}

.btn-page:hover:not(:disabled) {
  border-color: #3498db;
  color: #3498db;
}

.btn-page.active {
  background: #3498db;
  color: white;
  border-color: #3498db;
}

.btn-page:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 5px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease;
}

.btn-create {
  background: #27ae60;
  color: white;
}

.btn-create:hover {
  background: #229954;
  transform: translateY(-1px);
}

.btn-submit {
  background: #3498db;
  color: white;
}

.btn-submit:hover:not(:disabled) {
  background: #2980b9;
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-cancel {
  background: #95a5a6;
  color: white;
}

.btn-cancel:hover {
  background: #7f8c8d;
}

.btn-close {
  background: none;
  color: #7f8c8d;
  padding: 5px;
  border-radius: 4px;
}

.btn-close:hover {
  background: #f8f9fa;
  color: #2c3e50;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 15px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px;
  border-bottom: 1px solid #eee;
}

.modal-header h2 {
  margin: 0;
  color: #2c3e50;
  font-size: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-form {
  padding: 30px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #2c3e50;
}

.form-group label.required::after {
  content: ' *';
  color: #e74c3c;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #3498db;
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
}

.checkbox-group label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  margin-bottom: 0;
}

.checkbox-group input[type="checkbox"] {
  width: auto;
  margin: 0;
}

.checkmark {
  font-weight: normal;
}

.image-preview {
  margin-top: 10px;
  max-width: 200px;
}

.image-preview img {
  width: 100%;
  height: auto;
  border-radius: 8px;
  border: 2px solid #ddd;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

/* Responsive Design */
@media (max-width: 768px) {
  .admin-projects {
    padding: 15px;
  }
  
  .admin-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .admin-filters {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-actions {
    flex-direction: column;
  }
  
  .projects-grid {
    grid-template-columns: 1fr;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .modal-content {
    margin: 10px;
    max-height: calc(100vh - 20px);
  }
  
  .modal-header,
  .modal-form {
    padding: 20px;
  }
  
  .modal-actions {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .pagination {
    flex-wrap: wrap;
    gap: 5px;
  }
  
  .page-numbers {
    order: 3;
    width: 100%;
    justify-content: center;
  }
}
</style>